/**
 * useOpdrachtDetail Hook
 * Fetches an opdracht (multi-section challenge) with sections and completion status
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  Challenge,
  ChallengeSection,
  SectionCompletion,
  SectionWithStatus,
  OpdrachtWithProgress,
  OpdrachtConfig,
  CompletionStatus,
} from "../types";

interface UseOpdrachtDetailReturn {
  /** The opdracht with sections and progress */
  opdracht: OpdrachtWithProgress | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Refetch data */
  refetch: () => Promise<void>;
  /** Submit a section completion */
  submitSection: (
    sectionId: string,
    submission: Record<string, unknown>
  ) => Promise<boolean>;
  /** Update an existing section submission */
  updateSection: (
    sectionId: string,
    submission: Record<string, unknown>
  ) => Promise<boolean>;
}

/**
 * Hook for fetching opdracht details with sections and progress
 */
export const useOpdrachtDetail = (
  challengeId?: string
): UseOpdrachtDetailReturn => {
  const { activeProfile, userTeam } = useAuth();
  const [opdracht, setOpdracht] = useState<OpdrachtWithProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch opdracht with sections and completions
   */
  const fetchOpdracht = useCallback(async () => {
    if (!challengeId || !activeProfile?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch the challenge
      const { data: challengeData, error: challengeError } = await supabase
        .from("challenges")
        .select("*")
        .eq("id", challengeId)
        .single();

      if (challengeError) throw challengeError;

      // Verify it's an opdracht type
      if (challengeData.challenge_type !== "opdracht") {
        throw new Error("This challenge is not an opdracht");
      }

      // Fetch sections
      const { data: sectionsData, error: sectionsError } = await supabase
        .from("challenge_sections")
        .select("*")
        .eq("challenge_id", challengeId)
        .order("order_index", { ascending: true });

      if (sectionsError) throw sectionsError;

      const sections = (sectionsData || []) as ChallengeSection[];

      // Fetch section completions for this user/team
      const sectionIds = sections.map((s) => s.id);
      let completionsMap: Record<string, SectionCompletion> = {};

      if (sectionIds.length > 0) {
        let completionsQuery = supabase
          .from("section_completions")
          .select("*")
          .in("section_id", sectionIds);

        // Filter by user or team
        if (userTeam?.id) {
          completionsQuery = completionsQuery.or(
            `completed_by.eq.${activeProfile.id},team_id.eq.${userTeam.id}`
          );
        } else {
          completionsQuery = completionsQuery.eq("completed_by", activeProfile.id);
        }

        const { data: completionsData } = await completionsQuery;

        if (completionsData) {
          (completionsData as SectionCompletion[]).forEach((completion) => {
            completionsMap[completion.section_id] = completion;
          });
        }
      }

      // Get opdracht config
      const config = challengeData.config as OpdrachtConfig;

      // Build sections with status
      const sectionsWithStatus: SectionWithStatus[] = sections.map(
        (section, index) => {
          const completion = completionsMap[section.id] || null;
          const isCompleted =
            completion?.status === "approved" ||
            completion?.status === "auto_approved";
          
          // Check if can complete based on order requirement
          let canComplete = true;
          if (config.require_order && index > 0) {
            // Must complete previous sections first
            const prevSection = sections[index - 1];
            const prevCompletion = completionsMap[prevSection.id];
            const prevCompleted =
              prevCompletion?.status === "approved" ||
              prevCompletion?.status === "auto_approved";
            canComplete = prevCompleted;
          }

          // Can't complete if already completed or pending
          if (isCompleted || completion?.status === "pending") {
            canComplete = false;
          }

          return {
            ...section,
            completion,
            is_completed: isCompleted,
            can_complete: canComplete,
          };
        }
      );

      // Calculate progress
      const totalPoints = sections.reduce((sum, s) => sum + (s.points || 0), 0);
      const earnedPoints = sectionsWithStatus.reduce((sum, s) => {
        if (s.is_completed) {
          return sum + (s.completion?.points_earned || s.points || 0);
        }
        return sum;
      }, 0);
      const completedSections = sectionsWithStatus.filter(
        (s) => s.is_completed
      ).length;
      const totalSections = sections.length;
      const progressPercentage =
        totalSections > 0 ? (completedSections / totalSections) * 100 : 0;

      const opdrachtWithProgress: OpdrachtWithProgress = {
        ...challengeData,
        sections: sectionsWithStatus,
        total_points: totalPoints,
        earned_points: earnedPoints,
        completed_sections: completedSections,
        total_sections: totalSections,
        progress_percentage: progressPercentage,
      };

      setOpdracht(opdrachtWithProgress);
    } catch (err: any) {
      console.error("Error fetching opdracht:", err);
      setError(err.message || "Kon opdracht niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [challengeId, activeProfile?.id, userTeam?.id]);

  /**
   * Submit a section completion
   */
  const submitSection = useCallback(
    async (
      sectionId: string,
      submission: Record<string, unknown>
    ): Promise<boolean> => {
      if (!activeProfile?.id || !opdracht) return false;

      try {
        // Get section to determine points
        const section = opdracht.sections.find((s) => s.id === sectionId);
        if (!section) throw new Error("Section not found");

        // Determine status based on section config
        // If auto_approve is explicitly set in config, use that value
        // Otherwise, default to auto-approve for checkbox types only
        const sectionConfig = section.config as any;
        const hasExplicitAutoApprove = sectionConfig?.auto_approve !== undefined;
        
        let autoApprove: boolean;
        if (hasExplicitAutoApprove) {
          // Use the explicit setting from admin
          autoApprove = sectionConfig.auto_approve === true;
        } else {
          // Default behavior: checkbox types are auto-approved
          autoApprove = section.section_type === "checkbox";
        }

        const completionData = {
          section_id: sectionId,
          completed_by: activeProfile.id,
          team_id: userTeam?.id || null,
          submission,
          status: autoApprove ? "auto_approved" : "pending",
          points_earned: autoApprove ? section.points : 0,
          completed_at: new Date().toISOString(),
        };

        const { error: insertError } = await supabase
          .from("section_completions")
          .insert(completionData);

        if (insertError) throw insertError;

        // Refetch to update state
        await fetchOpdracht();

        return true;
      } catch (err: any) {
        console.error("Error submitting section:", err);
        setError(err.message || "Kon sectie niet indienen");
        return false;
      }
    },
    [activeProfile?.id, userTeam?.id, opdracht, fetchOpdracht]
  );

  /**
   * Update an existing section submission
   */
  const updateSection = useCallback(
    async (
      sectionId: string,
      submission: Record<string, unknown>
    ): Promise<boolean> => {
      if (!activeProfile?.id || !opdracht) return false;

      try {
        const section = opdracht.sections.find((s) => s.id === sectionId);
        if (!section?.completion) throw new Error("No existing completion to update");

        // Only allow updating pending submissions
        if (section.completion.status !== "pending") {
          throw new Error("Can only update pending submissions");
        }

        const { error: updateError } = await supabase
          .from("section_completions")
          .update({
            submission,
            completed_at: new Date().toISOString(),
          })
          .eq("id", section.completion.id);

        if (updateError) throw updateError;

        // Refetch to update state
        await fetchOpdracht();

        return true;
      } catch (err: any) {
        console.error("Error updating section:", err);
        setError(err.message || "Kon sectie niet bijwerken");
        return false;
      }
    },
    [activeProfile?.id, opdracht, fetchOpdracht]
  );

  // Initial fetch
  useEffect(() => {
    fetchOpdracht();
  }, [fetchOpdracht]);

  return {
    opdracht,
    isLoading,
    error,
    refetch: fetchOpdracht,
    submitSection,
    updateSection,
  };
};

export default useOpdrachtDetail;
